import React, { useMemo, useState, useRef, useLayoutEffect } from 'react';
import { createPortal } from 'react-dom';
import { ChevronDownIcon } from '@heroicons/react/24/solid';

/**
 * Group air item types by category → type for three-level dropdown.
 * Returns { category: { type: [items] } }
 */
function groupByCategryAndType(list) {
  const grouped = {};
  (list || []).forEach((item) => {
    if (!item.isActive) return;
    const cat = item.category || 'other';
    const typ = item.type || 'other';
    if (!grouped[cat]) grouped[cat] = {};
    if (!grouped[cat][typ]) grouped[cat][typ] = [];
    grouped[cat][typ].push(item);
  });
  return grouped;
}

/**
 * Three-layer dropdown: category → type → typeName.
 * @param {Array} list - Air item types with { id, category, type, typeName, isActive }
 * @param {string|number} value - Selected item id
 * @param {function(id)} onChange - Called with selected item id
 * @param {string} [placeholder] - Button placeholder when nothing selected
 * @param {boolean} [disabled]
 * @param {string} [label] - Label above the button
 * @param {string} [className] - Wrapper div class
 * @param {string} [buttonClassName] - Button class
 */
const AirItemTypeTripleDropdown = ({
  list = [],
  value,
  onChange,
  placeholder = 'Select type',
  disabled = false,
  label,
  className = '',
  buttonClassName = 'w-full flex items-center justify-between rounded-md border border-gray-300 bg-white px-3 py-2 text-left text-sm shadow-sm focus:border-indigo-500 focus:outline-none focus:ring-1 focus:ring-indigo-500',
}) => {
  const buttonRef = useRef(null);
  const [dropdownOpen, setDropdownOpen] = useState(false);
  const [openCategory, setOpenCategory] = useState(null);
  const [openType, setOpenType] = useState(null);
  const [dropdownPosition, setDropdownPosition] = useState({ top: 0, left: 0, width: 0 });

  const grouped = useMemo(() => groupByCategryAndType(list), [list]);
  const categories = useMemo(() => Object.keys(grouped).sort(), [grouped]);

  const selectedLabel = useMemo(() => {
    if (value == null || value === '') return '';
    const item = list.find((i) => String(i.id) === String(value));
    return item ? item.typeName : '';
  }, [list, value]);

  const handleSelect = (id) => {
    onChange(id);
    setDropdownOpen(false);
    setOpenCategory(null);
    setOpenType(null);
  };

  const handleClose = () => {
    setDropdownOpen(false);
    setOpenCategory(null);
    setOpenType(null);
  };

  // Update dropdown position when opened (for portal rendering - fixed position)
  // Constrain to viewport so dropdown fits on mobile (no wider than screen)
  useLayoutEffect(() => {
    if (dropdownOpen && buttonRef.current) {
      const rect = buttonRef.current.getBoundingClientRect();
      const vw = typeof window !== 'undefined' ? window.innerWidth : 480;
      const padding = 16;
      const preferredMin = Math.max(480, rect.width);
      const panelWidth = Math.min(preferredMin, vw - padding);
      const left = Math.max(padding / 2, Math.min(rect.left, vw - panelWidth - padding / 2));
      setDropdownPosition({
        top: rect.bottom,
        left,
        width: panelWidth,
      });
    }
  }, [dropdownOpen]);

  const dropdownPanel = dropdownOpen && (
    <>
      <div
        className="fixed inset-0 z-[9998]"
        aria-hidden="true"
        onClick={handleClose}
      />
      <div
        className="fixed z-[9999] flex rounded-md border border-gray-200 bg-white shadow-lg overflow-x-auto"
        style={{
          top: dropdownPosition.top + 4,
          left: dropdownPosition.left,
          width: dropdownPosition.width,
          maxHeight: 'min(70vh, 320px)',
        }}
      >
        {/* Level 1: Categories */}
        <ul className="py-1 border-r border-gray-100 min-w-[100px] shrink-0 max-h-[300px] overflow-y-auto">
          {categories.map((cat) => (
            <li
              key={cat}
              className="relative"
              onMouseEnter={() => { setOpenCategory(cat); setOpenType(null); }}
            >
              <button
                type="button"
                onClick={() => { setOpenCategory(openCategory === cat ? null : cat); setOpenType(null); }}
                className={`block w-full text-left px-3 py-2 text-sm cursor-pointer whitespace-nowrap ${
                  openCategory === cat ? 'bg-indigo-100 text-indigo-800 font-medium' : 'text-gray-700 hover:bg-gray-50'
                }`}
              >
                {cat}
              </button>
            </li>
          ))}
        </ul>

        {/* Level 2: Types (within selected category) */}
        {openCategory && grouped[openCategory] && (
          <ul className="py-1 border-r border-gray-100 min-w-[100px] shrink-0 max-h-[300px] overflow-y-auto">
            {Object.keys(grouped[openCategory]).sort().map((typ) => (
              <li
                key={typ}
                className="relative"
                onMouseEnter={() => setOpenType(typ)}
              >
                <button
                  type="button"
                  onClick={() => setOpenType(openType === typ ? null : typ)}
                  className={`block w-full text-left px-3 py-2 text-sm cursor-pointer whitespace-nowrap ${
                    openType === typ ? 'bg-indigo-100 text-indigo-800 font-medium' : 'text-gray-700 hover:bg-gray-50'
                  }`}
                >
                  {typ}
                </button>
              </li>
            ))}
          </ul>
        )}

        {/* Level 3: TypeNames (within selected type) */}
        {openCategory && openType && grouped[openCategory]?.[openType] && (
          <ul className="py-1 min-w-[120px] shrink-0 max-h-[300px] overflow-y-auto">
            {grouped[openCategory][openType].map((item) => (
              <li key={item.id}>
                <button
                  type="button"
                  onClick={() => handleSelect(item.id)}
                  className="block w-full text-left px-3 py-2 text-sm text-gray-700 hover:bg-indigo-50 hover:text-indigo-800"
                >
                  {item.typeName}
                </button>
              </li>
            ))}
          </ul>
        )}
      </div>
    </>
  );

  return (
    <div className={className}>
      {label && (
        <label className="block text-sm font-medium text-gray-700 mb-1">{label}</label>
      )}
      <div className="relative">
        <button
          ref={buttonRef}
          type="button"
          onClick={() => setDropdownOpen((o) => !o)}
          disabled={disabled}
          className={buttonClassName}
        >
          <span className={value != null && value !== '' ? 'text-gray-900' : 'text-gray-500'}>
            {selectedLabel || placeholder}
          </span>
          <ChevronDownIcon className="h-4 w-4 text-gray-400 shrink-0" />
        </button>
        {dropdownOpen && typeof document !== 'undefined' && createPortal(dropdownPanel, document.body)}
      </div>
    </div>
  );
};

export default AirItemTypeTripleDropdown;
